<script lang="ts">
  /**
   * Browse Mode (STEP_8_SPEC.md §2, D9): a read-only viewer over EVERY page of
   * the notebook, reached from the page footer's `[ Browse ]` button.
   *
   * Read-only by construction rather than by `pointer-events: none`: lines
   * take no activation — `TaskLine`'s click routes only through callbacks it is
   * passed, and none are passed here — no focus state (`focusedTaskId` is
   * always null), no footer, no capture — and lines are
   * handed to `PageCard` non-interactive, so nothing here is tab-focusable.
   * The keyboard layer is dead at the container (D8's guard); this component
   * renders and filters, and authors nothing.
   *
   * Search (D9) is a case-insensitive substring over task titles that hides
   * WHOLE pages: a page with zero matching tasks does not render, a matching
   * one renders complete — every slot, geometry intact. One `$derived` pass;
   * no index, no virtualisation (tens of pages, not thousands). State is
   * local and unpersisted.
   *
   * A page matches when ANY of its tasks matches; a matching page renders
   * COMPLETE, and its non-matching lines are greyed out (`line--dim`) rather
   * than hidden — the text stays in the DOM. By default a match ignores
   * worked tasks (Done/Dismissed); the checkbox re-includes them in
   * matching. Both share the one predicate below.
   */
  import type { Projection } from '../types/generated';
  import { tasksOnPage } from '../storage/fold';
  import Icon from './Icon.svelte';
  import ArrowLeft from 'lucide/dist/esm/icons/arrow-left.mjs';
  import PageCard from './PageCard.svelte';

  interface Props {
    projection: Projection;
    /** Task ids that are still unacknowledged predictions (§6.2). */
    pendingIds?: Set<string>;
    onBack: () => void;
  }

  let { projection, pendingIds = new Set(), onBack }: Props = $props();

  let query = $state('');
  let includeWorked = $state(false);

  const pages = $derived(
    Object.values(projection.pages).sort((a, b) => a.page_number - b.page_number),
  );

  const needle = $derived(query.trim().toLowerCase());

  // The one predicate, shared by the page filter and the dim signal: a task
  // matches when its title contains the needle and — unless the checkbox says
  // otherwise — it has not been worked (Done/Dismissed). An empty needle
  // matches everything, so `matchedIds` is null: no dimming, every page shown.
  const matchedIds = $derived.by(() => {
    if (needle === '') return null;
    const ids = new Set<string>();
    for (const page of pages) {
      for (const task of tasksOnPage(projection, page.id)) {
        if (!task.title.toLowerCase().includes(needle)) continue;
        if (!includeWorked && (task.state === 'Done' || task.state === 'Dismissed')) continue;
        ids.add(task.task_id);
      }
    }
    return ids;
  });

  const matching = $derived(
    needle === ''
      ? pages
      : pages.filter((page) =>
          tasksOnPage(projection, page.id).some((task) => matchedIds!.has(task.task_id)),
        ),
  );
</script>

<section class="browse-view" data-testid="browse-view">
  <header class="browse-header">
    <button class="action" type="button" data-testid="browse-back" onclick={onBack}>
      <Icon node={ArrowLeft} />
      [ Back ]
    </button>
    <input
      class="browse-search"
      type="search"
      placeholder="Search tasks"
      aria-label="Search tasks"
      data-testid="browse-search"
      bind:value={query}
    />
    <span class="browse-count">
      {matching.length}{matching.length === 1 ? ' page' : ' pages'}
    </span>
    <label class="browse-worked">
      <input
        type="checkbox"
        bind:checked={includeWorked}
        data-testid="browse-include-worked"
      />
      Include worked items
    </label>
  </header>

  {#if matching.length === 0}
    <p class="browse-empty" data-testid="browse-empty">No pages match</p>
  {:else}
    <div class="browse-pages">
      {#each matching as page (page.id)}
        <PageCard
          {page}
          {projection}
          {pendingIds}
          focusedTaskId={null}
          isFinalPage={page.status === 'Writing'}
          archived={true}
          interactive={false}
          matchingIds={matchedIds}
        />
      {/each}
    </div>
  {/if}
</section>

<style>
  .browse-view {
    width: min(100%, 60em);
    margin: 0 auto;
    padding: 0.75rem 0 3rem;
  }

  .browse-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1.25rem;
  }

  .browse-search {
    flex: 1;
    font: inherit;
    font-size: 16px;
    border: none;
    border-bottom: 1px solid color-mix(in srgb, var(--ink-muted) 70%, transparent);
    background: transparent;
    padding: 4px 2px;
  }

  .browse-count {
    font-size: 12px;
    color: var(--ink-muted);
    white-space: nowrap;
  }

  .browse-worked {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: var(--ink-muted);
    white-space: nowrap;
    user-select: none;
  }

  .browse-empty {
    color: var(--ink-muted);
    font-style: italic;
  }

  .browse-pages {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }
</style>
